/**
 * Dialog shells so every modal has the same structure and spacing:
 * title and optional description, a body with gap-5, and actions on the right.
 */
import { AlertDialog, Button, Form, Modal } from "@heroui/react";
import type { FormEvent, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { InlineError } from "./ui";

type Size = "sm" | "md" | "lg";

/** Modal with a form. Submitting runs `onSubmit`; Cancel closes it. */
export function FormDialog({
  isOpen,
  onOpenChange,
  title,
  description,
  size = "md",
  onSubmit,
  submitLabel,
  submitVariant = "primary",
  isPending,
  isSubmitDisabled,
  error,
  children,
}: {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  description?: ReactNode;
  size?: Size;
  onSubmit: () => void;
  submitLabel: string;
  submitVariant?: "primary" | "danger";
  isPending?: boolean;
  isSubmitDisabled?: boolean;
  error?: unknown;
  children: ReactNode;
}) {
  const { t } = useTranslation();
  const submit = (e: FormEvent) => {
    e.preventDefault();
    onSubmit();
  };
  return (
    <Modal.Backdrop isOpen={isOpen} onOpenChange={onOpenChange}>
      <Modal.Container size={size} scroll="inside">
        <Modal.Dialog className="gap-6">
          <Modal.CloseTrigger />
          <Modal.Header className="gap-1.5 pr-8">
            <Modal.Heading>{title}</Modal.Heading>
            {description ? <p className="text-sm leading-relaxed text-muted">{description}</p> : null}
          </Modal.Header>
          {/*
            With scroll="inside", HeroUI scrolls Modal.Body only when it is a flex child that may shrink.
            The form sits between the dialog and the body, so it must pass that down (min-h-0 flex-1);
            otherwise a long form grows past the screen and the footer becomes unreachable.
          */}
          <Form onSubmit={submit} className="flex min-h-0 flex-1 flex-col gap-6">
            <Modal.Body className="flex flex-col gap-5 text-foreground">
              {children}
              <InlineError error={error} />
            </Modal.Body>
            <Modal.Footer>
              <Button slot="close" variant="tertiary">
                {t("common.cancel")}
              </Button>
              <Button
                type="submit"
                variant={submitVariant}
                isPending={isPending}
                isDisabled={isSubmitDisabled}
              >
                {submitLabel}
              </Button>
            </Modal.Footer>
          </Form>
        </Modal.Dialog>
      </Modal.Container>
    </Modal.Backdrop>
  );
}

/** Modal that only shows information, with a single Close action. */
export function InfoDialog({
  isOpen,
  onOpenChange,
  title,
  size = "md",
  isDismissable = true,
  children,
}: {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  size?: Size;
  isDismissable?: boolean;
  children: ReactNode;
}) {
  const { t } = useTranslation();
  return (
    <Modal.Backdrop isOpen={isOpen} onOpenChange={onOpenChange} isDismissable={isDismissable}>
      <Modal.Container size={size} scroll="inside">
        <Modal.Dialog className="gap-6">
          {isDismissable ? <Modal.CloseTrigger /> : null}
          <Modal.Header className="pr-8">
            <Modal.Heading className="flex items-center gap-3">{title}</Modal.Heading>
          </Modal.Header>
          <Modal.Body className="flex flex-col gap-6 text-foreground">{children}</Modal.Body>
          <Modal.Footer>
            <Button slot="close" variant="secondary">
              {t("common.close")}
            </Button>
          </Modal.Footer>
        </Modal.Dialog>
      </Modal.Container>
    </Modal.Backdrop>
  );
}

/** Confirmation for destructive actions. */
export function ConfirmDialog({
  isOpen,
  onOpenChange,
  title,
  body,
  confirmLabel,
  onConfirm,
  isPending,
  error,
}: {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  body: string;
  confirmLabel: string;
  onConfirm: () => void;
  isPending?: boolean;
  error?: unknown;
}) {
  const { t } = useTranslation();
  return (
    <AlertDialog.Backdrop isOpen={isOpen} onOpenChange={onOpenChange}>
      <AlertDialog.Container>
        <AlertDialog.Dialog className="gap-5 sm:max-w-[440px]">
          <AlertDialog.CloseTrigger />
          <AlertDialog.Header>
            <AlertDialog.Icon status="danger" />
            <AlertDialog.Heading>{title}</AlertDialog.Heading>
          </AlertDialog.Header>
          <AlertDialog.Body className="flex flex-col gap-4">
            <p>{body}</p>
            <InlineError error={error} />
          </AlertDialog.Body>
          <AlertDialog.Footer>
            <Button slot="close" variant="tertiary">
              {t("common.cancel")}
            </Button>
            <Button variant="danger" isPending={isPending} onPress={onConfirm}>
              {confirmLabel}
            </Button>
          </AlertDialog.Footer>
        </AlertDialog.Dialog>
      </AlertDialog.Container>
    </AlertDialog.Backdrop>
  );
}
